/**
 * SemesterReport — everything a semester holds, in report form.
 * Timetable load · attendance · fees billed/collected · results & GPA.
 * Every section exports to CSV.
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { useAcademicContext } from '@/components/school/AcademicContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Download, Clock, ClipboardCheck, Receipt, Trophy, Printer, MessageSquareQuote } from 'lucide-react';
import { authHeaders } from '@/lib/authToken';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** PDFs need the auth header, so fetch as a blob and open the object URL. */
const openPdf = async (path) => {
  const res = await fetch(`${API_URL}/api/school${path}`, { headers: authHeaders() });
  if (!res.ok) {
    toast.error('Could not build that PDF');
    return;
  }
  const url = URL.createObjectURL(await res.blob());
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

const slug = (s) => String(s || 'semester').trim().replace(/[^A-Za-z0-9._-]+/g, '_');

const toCsv = (rows, filename) => {
  if (!rows.length) return;
  const cols = Object.keys(rows[0]);
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const csv = [cols.join(','), ...rows.map((r) => cols.map((c) => esc(r[c])).join(','))].join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
  a.download = slug(filename);
  a.click();
  URL.revokeObjectURL(a.href);
};

const Stat = ({ label, value, testid }) => (
  <div className="rounded-lg border bg-white px-3 py-2" data-testid={testid}>
    <p className="text-[11px] uppercase tracking-wide text-slate-500">{label}</p>
    <p className="text-lg font-semibold text-slate-900">{value}</p>
  </div>
);

const Section = ({ icon: Icon, title, children, onExport, exportLabel, testid }) => (
  <Card data-testid={testid}>
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
      <CardTitle className="text-base flex items-center gap-2">
        <Icon className="h-4 w-4 text-slate-400" /> {title}
      </CardTitle>
      {onExport && (
        <Button size="sm" variant="outline" onClick={onExport} data-testid={`${testid}-export`}>
          <Download className="h-3.5 w-3.5 mr-1" /> {exportLabel || 'CSV'}
        </Button>
      )}
    </CardHeader>
    <CardContent className="space-y-4">{children}</CardContent>
  </Card>
);

const RATINGS = ['', 'excellent', 'very_good', 'good', 'fair', 'poor'];

const CommentsDialog = ({ tenantId, termId, student, onClose }) => {
  const qc = useQueryClient();
  const [form, setForm] = useState(null);

  const { data } = useQuery({
    queryKey: ['semester-comments', tenantId, termId, student?.student_id],
    queryFn: async () => (await api.get(
      `/school/tenants/${tenantId}/terms/${termId}/report-comments`,
      { params: { student_id: student.student_id } })).data,
    enabled: !!student,
  });

  const current = form || data;
  const set = (k) => (e) => setForm({ ...(current || {}), [k]: e.target.value });

  const save = useMutation({
    mutationFn: () => api.put(
      `/school/tenants/${tenantId}/terms/${termId}/report-comments/${student.student_id}`,
      {
        class_teacher_comment: current.class_teacher || '',
        conduct: current.conduct || '',
        effort: current.effort || '',
        ...(data?.can_write_head_comment
          ? { head_teacher_comment: current.head_teacher || '' } : {}),
      }),
    onSuccess: () => {
      toast.success('Comments saved — they print on the report card');
      qc.invalidateQueries({ queryKey: ['semester-comments', tenantId, termId] });
      onClose();
    },
    onError: (e) => toast.error(e.response?.data?.detail || 'Could not save the comments'),
  });

  return (
    <Dialog open={!!student} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="semester-comments-dialog">
        <DialogHeader>
          <DialogTitle>Report card comments · {student?.student_name}</DialogTitle>
        </DialogHeader>
        {!current ? <Skeleton className="h-32 w-full" /> : (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              {[['conduct', 'Conduct'], ['effort', 'Effort']].map(([key, label]) => (
                <div key={key} className="space-y-1.5">
                  <span className="text-slate-600">{label}</span>
                  <select value={current[key] || ''} onChange={set(key)}
                          data-testid={`comment-${key}`}
                          className="h-9 w-full rounded-md border border-slate-200 px-2 text-sm capitalize">
                    {RATINGS.map((r) => (
                      <option key={r} value={r}>{r ? r.replace('_', ' ') : 'Not rated'}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
            <div className="space-y-1.5">
              <span className="text-slate-600">Class teacher's comment</span>
              <Textarea rows={3} value={current.class_teacher || ''}
                        onChange={set('class_teacher')} data-testid="comment-class-teacher"
                        placeholder="A steady semester — strongest in the sciences." />
            </div>
            <div className="space-y-1.5">
              <span className="text-slate-600">Head teacher's comment</span>
              <Textarea rows={3} value={current.head_teacher || ''}
                        onChange={set('head_teacher')} data-testid="comment-head-teacher"
                        disabled={data && !data.can_write_head_comment}
                        placeholder="Keep it up next semester." />
              {data && !data.can_write_head_comment && (
                <p className="text-[11px] text-slate-500">
                  Only the head teacher or principal can write this line.
                </p>
              )}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={!current || save.isPending}
                  data-testid="semester-comments-save">
            {save.isPending ? 'Saving…' : 'Save comments'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const SemesterReport = ({ tenantId }) => {
  const { termId, term, year, semesters } = useAcademicContext(tenantId);
  const [commenting, setCommenting] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['semester-report', tenantId, termId],
    queryFn: async () => (await api.get(`/school/tenants/${tenantId}/semester-report`,
      { params: { term_id: termId } })).data,
    enabled: !!tenantId && !!termId,
  });

  if (!termId) {
    return (
      <Card><CardContent className="py-10 text-center text-sm text-slate-500"
                          data-testid="semester-report-no-semester">
        {semesters.length === 0
          ? `${year?.name || 'This academic year'} has no semesters yet — add one under Academic Years.`
          : 'Pick a semester in the bar above to see its report.'}
      </CardContent></Card>
    );
  }

  if (isLoading || !data) {
    return <Card><CardContent className="py-6 space-y-3">
      {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-14 w-full" />)}
    </CardContent></Card>;
  }

  const { timetable, attendance, fees, results } = data;
  const money = (n) => (n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

  return (
    <div className="space-y-4" data-testid="semester-report">
      <div className="flex items-center gap-2 flex-wrap">
        <h2 className="text-lg font-semibold text-slate-900">
          {data.semester.name} report
        </h2>
        <Badge variant="outline">{data.academic_year?.name}</Badge>
        {data.semester.is_current && <Badge className="bg-green-100 text-green-700">Current</Badge>}
        <span className="text-xs text-slate-500">
          {data.semester.start_date} → {data.semester.end_date}
        </span>
        <Button size="sm" variant="outline" className="ml-auto"
                data-testid="semester-summary-print"
                onClick={() => openPdf(`/tenants/${tenantId}/terms/${termId}/summary-sheet`)}>
          <Printer className="h-3.5 w-3.5 mr-1" /> Print summary
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Timetable periods" value={timetable.total_slots} testid="report-stat-timetable" />
        <Stat label="Attendance rate" value={`${attendance.attendance_rate}%`} testid="report-stat-attendance" />
        <Stat label="Fees collected" value={money(fees.collected)} testid="report-stat-collected" />
        <Stat label="Average GPA" value={results.average_gpa ?? '—'} testid="report-stat-gpa" />
      </div>

      <Section icon={Clock} title="Semester timetable" testid="report-timetable"
               onExport={() => toCsv(timetable.classes, `timetable-${data.semester.name}.csv`)}>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Stat label="Periods scheduled" value={timetable.total_slots} />
          <Stat label="Classes covered" value={timetable.classes.length} />
          <Stat label="Subjects used" value={timetable.subjects_used} />
        </div>
        {timetable.teachers.length > 0 && (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Lecturer</TableHead><TableHead className="text-right">Periods / week</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {timetable.teachers.map((t) => (
                <TableRow key={t.teacher_id}>
                  <TableCell>{t.teacher_name}</TableCell>
                  <TableCell className="text-right">{t.periods}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>

      <Section icon={ClipboardCheck} title="Semester attendance" testid="report-attendance"
               onExport={() => toCsv(
                 Object.entries(attendance.by_status).map(([status, count]) => ({ status, count })),
                 `attendance-${data.semester.name}.csv`)}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Sessions" value={attendance.sessions} />
          <Stat label="Marks recorded" value={attendance.records} />
          <Stat label="Attendance rate" value={`${attendance.attendance_rate}%`} />
          <Stat label="Absent" value={attendance.by_status?.absent || 0} />
        </div>
      </Section>

      <Section icon={Receipt} title="Semester fees" testid="report-fees"
               onExport={() => toCsv([{
                 semester: data.semester.name, structures: fees.structures, charges: fees.charges,
                 students_billed: fees.students_billed, billed: fees.billed,
                 collected: fees.collected, outstanding: fees.outstanding,
                 collection_rate: fees.collection_rate,
               }], `fees-${data.semester.name}.csv`)}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Billed" value={money(fees.billed)} testid="report-fees-billed" />
          <Stat label="Collected" value={money(fees.collected)} />
          <Stat label="Outstanding" value={money(fees.outstanding)} />
          <Stat label="Collection rate" value={`${fees.collection_rate}%`} />
        </div>
        <p className="text-xs text-slate-500">
          {fees.structures} fee structure(s) apply to this semester
          {fees.annual_structures ? ` (${fees.annual_structures} annual)` : ''} ·
          {' '}{fees.students_billed} student(s) billed
        </p>
      </Section>

      <Section icon={Trophy} title="Semester results & GPA" testid="report-results"
               onExport={() => toCsv(results.students, `results-${data.semester.name}.csv`)}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Exams" value={results.exams.length} />
          <Stat label="Marks entered" value={results.marks_entered} />
          <Stat label="Class average" value={`${results.class_average_percent}%`} />
          <Stat label="Average GPA" value={results.average_gpa ?? '—'} />
        </div>
        {results.students.length > 0 ? (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Student</TableHead>
              <TableHead className="text-right">Subjects</TableHead>
              <TableHead className="text-right">Average</TableHead>
              <TableHead className="text-right">GPA</TableHead>
              <TableHead className="text-right">Position</TableHead>
              <TableHead className="text-right">Report card</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {results.students.slice(0, 25).map((s) => (
                <TableRow key={s.student_id} data-testid={`report-student-${s.student_id}`}>
                  <TableCell>{s.student_name}</TableCell>
                  <TableCell className="text-right">{s.subjects}</TableCell>
                  <TableCell className="text-right">{s.average_percent}%</TableCell>
                  <TableCell className="text-right">{s.gpa ?? '—'}</TableCell>
                  <TableCell className="text-right" data-testid={`report-position-${s.student_id}`}>
                    {s.class_position
                      ? `${s.class_position} of ${s.class_size}`
                      : '—'}
                    {s.overall_position && (
                      <span className="block text-[11px] text-slate-400">
                        {s.overall_position} of {s.cohort_size} overall
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right space-x-1 whitespace-nowrap">
                    <Button size="sm" variant="ghost" className="h-7 text-xs"
                            data-testid={`report-comments-${s.student_id}`}
                            onClick={() => setCommenting(s)}>
                      <MessageSquareQuote className="h-3.5 w-3.5 mr-1" /> Comments
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs"
                            data-testid={`report-card-${s.student_id}`}
                            onClick={() => openPdf(
                              `/tenants/${tenantId}/terms/${termId}/report-card/${s.student_id}`)}>
                      <Printer className="h-3.5 w-3.5 mr-1" /> Print
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <p className="text-sm text-slate-500" data-testid="report-results-empty">
            No marks captured for this semester yet.
          </p>
        )}
      </Section>

      {commenting && (
        <CommentsDialog tenantId={tenantId} termId={termId} student={commenting}
                        onClose={() => setCommenting(null)} />
      )}
    </div>
  );
};

export default SemesterReport;
